/**
 * Coordinate Panel Component
 * Export and import map coordinates
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Download, Upload, FileJson, FileSpreadsheet } from 'lucide-react';
import {
  exportMapCoordinates,
  exportMapCoordinatesCSV,
  importMapCoordinates,
} from '@/lib/admin/map-coordinates';
import { useToast } from '@/hooks/use-toast';

export function CoordinatePanel({ onImportComplete }: { onImportComplete?: () => void }) {
  const { toast } = useToast();
  const [importData, setImportData] = useState('');
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleExportJSON = async () => {
    setExporting(true);
    try {
      const { data, error } = await exportMapCoordinates();
      if (error) {
        toast({
          title: 'Export Failed',
          description: error,
          variant: 'destructive',
        });
        return;
      }

      if (data) {
        const blob = new Blob([data], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `map-coordinates-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast({
          title: 'Export Successful',
          description: 'Map coordinates exported to JSON file',
        });
      }
    } catch (error) {
      toast({
        title: 'Export Failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setExporting(false);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const { data, error } = await exportMapCoordinatesCSV();
      if (error) {
        toast({
          title: 'Export Failed',
          description: error,
          variant: 'destructive',
        });
        return;
      }

      if (data) {
        const blob = new Blob([data], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `map-coordinates-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast({
          title: 'Export Successful',
          description: 'Map coordinates exported to CSV file',
        });
      }
    } catch (error) {
      toast({
        title: 'Export Failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async () => {
    if (!importData.trim()) {
      toast({
        title: 'Import Failed',
        description: 'Please paste JSON data to import',
        variant: 'destructive',
      });
      return;
    }

    setImporting(true);
    try {
      const result = await importMapCoordinates(importData);
      if (result.success) {
        toast({
          title: 'Import Successful',
          description: `Imported coordinates for ${result.importedCount || 0} units`,
        });
        setImportData('');
        onImportComplete?.();
      } else {
        toast({
          title: 'Import Failed',
          description: result.error || 'Unknown error',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Import Failed',
        description: error instanceof Error ? error.message : 'Invalid JSON format',
        variant: 'destructive',
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Export / Import</CardTitle>
        <CardDescription className="text-xs">Export to file or import from JSON</CardDescription>
      </CardHeader>
      <CardContent className="pt-0 space-y-3 sm:space-y-4">
        {/* Export */}
        <div>
          <h4 className="text-xs sm:text-sm font-medium mb-2">Export</h4>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportJSON}
              disabled={exporting}
              className="text-xs sm:text-sm h-8 sm:h-9 flex-1 min-w-[120px] sm:min-w-[140px]"
            >
              <FileJson className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 shrink-0" />
              <span className="hidden sm:inline">Export JSON</span>
              <span className="sm:hidden">JSON</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCSV}
              disabled={exporting}
              className="text-xs sm:text-sm h-8 sm:h-9 flex-1 min-w-[120px] sm:min-w-[140px]"
            >
              <FileSpreadsheet className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 shrink-0" />
              <span className="hidden sm:inline">Export CSV</span>
              <span className="sm:hidden">CSV</span>
            </Button>
          </div>
        </div>

        {/* Import */}
        <div>
          <h4 className="text-xs sm:text-sm font-medium mb-2">Import</h4>
          <Textarea
            placeholder="Paste JSON data here..."
            value={importData}
            onChange={(e) => setImportData(e.target.value)}
            className="min-h-[80px] sm:min-h-[100px] font-mono text-xs resize-none"
          />
          <Button
            variant="default"
            size="sm"
            onClick={handleImport}
            disabled={importing || !importData.trim()}
            className="mt-2 w-full sm:w-auto text-xs sm:text-sm h-8 sm:h-9"
          >
            <Upload className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
            {importing ? 'Importing...' : 'Import JSON'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

